import { useMemo, useRef } from 'react';
import {
	useFloating,
	autoUpdate,
	offset,
	flip,
	shift,
	useClick,
	useDismiss,
	useRole,
	useInteractions,
	Placement,
	arrow
} from '@floating-ui/react';
import useControllableState from './use-controllable-state';

type ArgumentTypes<F> = F extends (...args: infer A) => unknown ? A : never;

type MiddlewareOptions = Partial<{
	offset: ArgumentTypes<typeof offset>[0];
	flip: ArgumentTypes<typeof flip>[0];
	shift: ArgumentTypes<typeof shift>[0];
	arrow: ArgumentTypes<typeof arrow>[0];
}>;

export type FloatingElementOptions = {
	defaultOpen?: boolean;
	placement?: Placement;
	isModal?: boolean;
	isOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	dissmissOptions?: {
		outsidePressEvent?: 'mousedown' | 'pointerdown' | 'click';
		outsidePress?: (event: MouseEvent) => boolean;
	};
	middlewareOptions?: MiddlewareOptions;
	strategy?: 'absolute' | 'fixed';
};

/**
 * @title useFloatingElement
 * @description
 * A hook that enables floating element functionality but with a more opinionated API from the floating-ui package.
 * The package makes it a lot easier to create floating components such as Popovers, Modals, Tooltips etc. because it handles all the positioning logic for you.
 *
 * @param defaultOpen - Determines if the Popover should be open by default.
 * @param isOpen - Determines if the Popover is open or closed. This is useful if you want to control the Popover state manually.
 * @param onOpenChange - Callback that is called when the Popover is opened or closed.
 * @param placement - Determines where the Popover should be placed relative to the trigger element.
 * @param isModal - Determines if focus is “modal”, meaning focus is fully trapped inside the floating element and outside content cannot be accessed. This includes screen reader virtual cursors. See https://floating-ui.com/docs/floatingfocusmanager#modal for more information.
 * @param dissmissOptions - Customize the dismiss functionality. See https://floating-ui.com/docs/dismiss for more information.
 * @param middlewareOptions - Customize the middleware functions. See https://floating-ui.com/docs/middleware for more information.
 */

export default function useFloatingElement({
	defaultOpen = false,
	isOpen: controlledOpen,
	onOpenChange: setControlledOpen,
	placement = 'bottom',
	isModal,
	dissmissOptions = {
		outsidePressEvent: 'mousedown'
	},
	middlewareOptions = {
		offset: 6,
		flip: { fallbackAxisSideDirection: 'end', padding: 6 },
		shift: { padding: 6 },
		arrow: { element: null }
	},
	strategy
}: FloatingElementOptions = {}) {
	const arrowRef = useRef(null);

	const [open = false, setOpen] = useControllableState({
		controlledState: controlledOpen,
		defaultState: defaultOpen,
		onChange: isOpen => {
			setControlledOpen?.(isOpen);
		}
	});

	const data = useFloating({
		placement,
		open,
		onOpenChange: setOpen,
		whileElementsMounted: autoUpdate,
		middleware: [
			// Offset the floating element from the trigger element
			offset(middlewareOptions.offset),
			// Flip the floating element to the opposite side if there's not enough space
			flip(middlewareOptions.flip),
			// Shift the floating element to avoid overlapping with the trigger element
			shift(middlewareOptions.shift),
			// Add an arrow to the floating element pointing to the trigger element
			arrow({ ...middlewareOptions.arrow, element: arrowRef })
		],
		strategy
	});

	const { context } = data;

	const click = useClick(context, {
		enabled: controlledOpen == null
	});
	const dismiss = useDismiss(context, dissmissOptions);
	const role = useRole(context);

	const interactions = useInteractions([click, dismiss, role]);

	return useMemo(
		() => ({
			isOpen: open,
			setIsOpen: setOpen,
			...interactions,
			...data,
			isModal,
			arrowRef
		}),
		[open, setOpen, interactions, data, isModal]
	);
}
